import { useCallback, useEffect, useState } from "react";
import { Link, useRoute } from "wouter";
import axios from "axios";
import { AlertTriangle, ArrowLeft, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { JitsiConsentDialog } from "@/components/JitsiConsentDialog";
import { JitsiMeeting } from "@/components/JitsiMeeting";
import { ModeratorBadge } from "@/components/ModeratorBadge";
import { useAuth } from "@/contexts/AuthContext";
import {
  getApiErrorDetail,
  type MeetingTokenResponse,
  requestMeetingToken,
} from "@/lib/api";

type Status =
  | { kind: "loading" }
  | { kind: "forbidden"; detail: string }
  | { kind: "consent_required"; detail: string }
  | { kind: "error"; detail: string }
  | { kind: "ready"; meeting: MeetingTokenResponse };

// Page Audience visio. Volontairement HORS du <ArbiterShell> : la salle
// Jitsi reclame la pleine surface (immersif). On herite tout de meme du
// theme dark AAA en posant data-workspace="arbitre" sur la racine -- les
// composants shadcn et les variables proof-gold / graphite cohabitent
// avec le reste de l'app.
export default function Audience() {
  const [match, params] = useRoute<{ caseId: string }>("/audiences/:caseId");
  const { session } = useAuth();
  const [status, setStatus] = useState<Status>({ kind: "loading" });
  const [consentOpen, setConsentOpen] = useState(false);

  const caseIdRaw = params?.caseId;
  const caseId = caseIdRaw ? Number.parseInt(caseIdRaw, 10) : Number.NaN;

  const displayName =
    (session?.user.user_metadata as { full_name?: string } | undefined)
      ?.full_name ??
    session?.user.email ??
    undefined;

  const fetchToken = useCallback(async () => {
    if (!Number.isFinite(caseId)) {
      setStatus({ kind: "error", detail: "Identifiant de dossier invalide." });
      return;
    }
    setStatus({ kind: "loading" });
    try {
      const meeting = await requestMeetingToken(caseId);
      setStatus({ kind: "ready", meeting });
    } catch (err) {
      const detail = getApiErrorDetail(err) ?? "Erreur inattendue.";
      if (axios.isAxiosError(err)) {
        const code = err.response?.status;
        if (code === 412) {
          setStatus({ kind: "consent_required", detail });
          setConsentOpen(true);
          return;
        }
        if (code === 403) {
          setStatus({ kind: "forbidden", detail });
          return;
        }
      }
      setStatus({ kind: "error", detail });
    }
  }, [caseId]);

  useEffect(() => {
    void fetchToken();
  }, [fetchToken]);

  if (!match) return null;

  return (
    <div
      data-workspace="arbitre"
      className="min-h-screen flex flex-col bg-background text-foreground"
    >
      <header
        className="flex items-center justify-between px-4 sm:px-6 py-3 border-b gap-3"
        style={{
          borderColor: "var(--graphite-600)",
          background: "var(--graphite-900)",
        }}
      >
        <Link
          href={`/cases/${caseIdRaw}`}
          className="inline-flex items-center gap-2 text-caption-arbitre"
          style={{ color: "var(--proof-gold)" }}
        >
          <ArrowLeft size={12} />
          Retour au dossier
        </Link>
        <div
          className="flex items-center gap-3 text-sm font-semibold"
          style={{ letterSpacing: "-0.01em" }}
        >
          Audience &middot; Dossier #{caseIdRaw}
          {status.kind === "ready" && status.meeting.is_moderator && (
            <ModeratorBadge />
          )}
        </div>
        <div className="w-32" />
      </header>

      <main className="flex-1 flex flex-col">
        {status.kind === "loading" && (
          <CenterMessage
            icon={
              <Loader2
                className="animate-spin"
                size={28}
                style={{ color: "var(--proof-gold)" }}
              />
            }
          >
            Vérification de l'accès à l'audience…
          </CenterMessage>
        )}

        {status.kind === "forbidden" && (
          <CenterMessage
            icon={
              <AlertTriangle
                size={28}
                style={{ color: "var(--block-red)" }}
              />
            }
          >
            <p
              className="max-w-md text-sm"
              style={{ color: "var(--arbitre-slate-300)" }}
            >
              Accès refusé à l'audience.
            </p>
            <p
              className="mt-2 max-w-md text-xs italic"
              style={{ color: "var(--arbitre-slate-500)" }}
            >
              {status.detail}
            </p>
          </CenterMessage>
        )}

        {status.kind === "consent_required" && (
          <CenterMessage
            icon={
              <AlertTriangle
                size={28}
                style={{ color: "var(--proof-gold)" }}
              />
            }
          >
            <p
              className="mb-4 max-w-md text-sm"
              style={{ color: "var(--arbitre-slate-300)" }}
            >
              {status.detail}
            </p>
            <Button onClick={() => setConsentOpen(true)}>
              Ouvrir la modale de consentement
            </Button>
          </CenterMessage>
        )}

        {status.kind === "error" && (
          <CenterMessage
            icon={
              <AlertTriangle
                size={28}
                style={{ color: "var(--block-red)" }}
              />
            }
          >
            <p
              className="max-w-md text-sm"
              style={{ color: "var(--arbitre-slate-300)" }}
            >
              {status.detail}
            </p>
            <Button className="mt-4" onClick={() => void fetchToken()}>
              Réessayer
            </Button>
          </CenterMessage>
        )}

        {status.kind === "ready" && (
          <div className="flex-1 flex flex-col">
            {status.meeting.simulated && (
              <div
                className="px-4 py-2 text-xs text-center"
                style={{
                  background:
                    "color-mix(in oklch, var(--alert-orange) 18%, transparent)",
                  color: "var(--alert-orange)",
                }}
              >
                Mode simulation Jitsi : le JWT est signé avec un secret de
                test, la salle ne sera pas acceptée par un serveur Jitsi de
                production.
              </div>
            )}
            <div className="flex-1">
              <JitsiMeeting
                domain={status.meeting.domain}
                roomName={status.meeting.room_name}
                jwt={status.meeting.token}
                displayName={displayName}
                onLeave={() => setStatus({ kind: "loading" })}
              />
            </div>
          </div>
        )}
      </main>

      {Number.isFinite(caseId) && (
        <JitsiConsentDialog
          open={consentOpen}
          caseId={caseId}
          onOpenChange={setConsentOpen}
          onConsented={() => {
            setConsentOpen(false);
            void fetchToken();
          }}
        />
      )}
    </div>
  );
}

function CenterMessage({
  icon,
  children,
}: {
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="flex-1 flex flex-col items-center justify-center gap-4 px-6 py-16 text-center">
      {icon}
      <div className="flex flex-col items-center">{children}</div>
    </div>
  );
}
